import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import Modal from 'components/Modal';
import { Box, Flex } from 'components/ui';
import {
    colors, space, mediaQueries, borders, radii
} from 'style/config';
import ProductCardZone from 'components/Catalog/ProductGrid/CompareModal/Zones/ProductCardZone';
import ColorsZone from 'components/Catalog/ProductGrid/CompareModal/Zones/ColorsZone';
import SizesZone from 'components/Catalog/ProductGrid/CompareModal/Zones/SizesZone';
import RatingsAndReviewsZone from 'components/Catalog/ProductGrid/CompareModal/Zones/RatingsAndReviewsZone';
import OverviewZone from 'components/Catalog/ProductGrid/CompareModal/Zones/OverviewZone';
import IngredientsZone from 'components/Catalog/ProductGrid/CompareModal/Zones/IngredientsZone';
import PickupAndDeliveryZone from 'components/Catalog/ProductGrid/CompareModal/Zones/PickupAndDeliveryZone';
import useScrollSync from 'components/Catalog/ProductGrid/hooks/useScrollSync';
import useScrollThreshold from 'components/Catalog/ProductGrid/hooks/useScrollThreshold';
import useScrollChainPrevention from 'components/Catalog/ProductGrid/hooks/useScrollChainPrevention';
import useComparisonFetch from 'components/Catalog/ProductGrid/hooks/useComparisonFetch';
import useComparisonData from 'components/Catalog/ProductGrid/hooks/useComparisonData';
import { useComparisonSkus } from 'components/Catalog/ProductGrid/hooks/useComparisonSkus';

const CompareModal = ({
    isOpen, onDismiss, getText = () => {}, selectedProducts = [], handleProductRemove = () => {}
}) => {
    const comparisonData = useComparisonData();
    const skus = useComparisonSkus(comparisonData);
    // Scroll synchronization between sticky header and content
    const { stickyRef, contentRef, syncFromSticky, syncFromContent } = useScrollSync();

    // Track scroll position for compact mode
    const { isScrolled, handleScroll: handleThresholdScroll, scrollRef } = useScrollThreshold(1, 173, 50);

    // Prevent scroll chaining to body
    useScrollChainPrevention(scrollRef, isOpen);

    // Fetch comparison data
    useComparisonFetch(isOpen, selectedProducts, onDismiss);

    // Combine scroll handlers for content area
    const handleContentScroll = useCallback(() => {
        syncFromContent();
        handleThresholdScroll();
    }, [syncFromContent, handleThresholdScroll]);

    // Callback ref to assign both contentRef and scrollRef to the same element
    const setContentRefs = useCallback(
        node => {
            contentRef.current = node;
            scrollRef.current = node;
        },
        [contentRef, scrollRef]
    );

    if (!isOpen) {
        return null;
    }

    const customStyle = {
        inner: {
            width: '100%',
            height: '100%',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            [mediaQueries.sm]: {
                width: '80%',
                height: '90%',
                borderRadius: radii[2]
            }
        }
    };

    const showColorsZone = skus?.some(sku => !!sku?.color);

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            width='80%'
            showDismiss={true}
            hasBodyScroll={true}
            margin='auto'
            customStyle={customStyle}
            dataAt='compare_modal'
        >
            {/* Sticky Header */}
            <Modal.Header baseCss={styles.stickyHeader}>
                <Flex css={styles.headerContent}>
                    <Box css={styles.headerSpacer} />
                    <Box css={styles.headerTitleContainer}>
                        <Modal.Title>{getText('compareProducts')}</Modal.Title>
                    </Box>
                    <Box css={styles.headerSpacer} />
                </Flex>
            </Modal.Header>

            {/* Scrollable Body with Horizontal Scrolling */}
            <Modal.Body style={styles.bodyContainer}>
                {/* Sticky Product Card Zone - FER-208 */}
                <ProductCardZone
                    comparisonData={comparisonData}
                    handleProductRemove={handleProductRemove}
                    getText={getText}
                    scrollRef={stickyRef}
                    onScroll={syncFromSticky}
                    isCompact={isScrolled}
                />

                <Box
                    ref={setContentRefs}
                    css={styles.scrollableContainer}
                    onScroll={handleContentScroll}
                    data-at={Sephora.debug.dataAt('compare_modal_scrollable_container')}
                >
                    {/* Colors Zone - FER-245 */}
                    {showColorsZone && (
                        <ColorsZone
                            skus={skus}
                            getText={getText}
                        />
                    )}

                    {/* Sizes Zone - FER-248 */}
                    <SizesZone
                        skus={skus}
                        getText={getText}
                    />

                    {/* Ratings and Reviews Zone - FER-252, FER-258 */}
                    <RatingsAndReviewsZone
                        skus={skus}
                        getText={getText}
                    />

                    {/* Overview Zone - Jira TBD */}
                    <OverviewZone
                        skus={skus}
                        getText={getText}
                    />

                    {/* Ingredients Zone - Jira TBD */}
                    <IngredientsZone
                        skus={skus}
                        getText={getText}
                    />

                    {/* Pickup & Delivery Zone - Jira TBD */}
                    <PickupAndDeliveryZone
                        skus={skus}
                        getText={getText}
                    />
                </Box>
            </Modal.Body>
        </Modal>
    );
};

const styles = {
    stickyHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: colors.white,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: `${space[4]}px`,
        paddingBottom: `${space[4]}px`,
        paddingLeft: `${space[4]}px`,
        // Add extra right padding to account for X button area (X button is absolutely positioned)
        // X button: 13px icon + padding on both sides
        paddingRight: `${space[4] + 13 + space[4]}px`,
        borderBottom: `${borders[1]} ${colors.lightGray}`,
        [mediaQueries.sm]: {
            paddingRight: `${space[5] + 13 + space[5]}px`,
            paddingLeft: `${space[5]}px`,
            borderTopRightRadius: radii[2],
            borderTopLeftRadius: radii[2]
        }
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        position: 'relative'
    },
    headerSpacer: {
        width: '40px',
        flexShrink: 0,
        // Match the width of the X button area to balance the layout
        // X button is 13px icon + padding (space[4] = 16px on each side)
        [mediaQueries.sm]: {
            width: '48px'
        }
    },
    headerTitleContainer: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
    },
    bodyContainer: {
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        paddingRight: `${space[4]}px`,
        paddingLeft: `${space[4]}px`,
        paddingTop: 0
    },
    scrollableContainer: {
        flex: 1,
        overflow: 'auto',
        overflowX: 'scroll',
        paddingX: `${space[4]}px`,
        paddingY: `${space[6]}px`,
        // Hide scrollbars but allow scrolling
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none'
        },
        // On mobile, allow horizontal scrolling (show 1-2 products at a time)
        [mediaQueries.xsMax]: {
            scrollSnapType: 'x mandatory'
        },
        [mediaQueries.sm]: {
            paddingX: `${space[4]}px`,
            scrollSnapType: 'x mandatory',
            overflow: 'scroll'
        }
    }
};

CompareModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onDismiss: PropTypes.func.isRequired,
    getText: PropTypes.func.isRequired,
    selectedProducts: PropTypes.arrayOf(PropTypes.object),
    comparisonData: PropTypes.object,
    onFetchComparison: PropTypes.func,
    handleProductRemove: PropTypes.func
};

export default wrapFunctionalComponent(CompareModal, 'CompareModal');
